import { Lazy } from '@studio/js-utils/async';
import type { Store } from '@studio/react-utils/store';
import type { T_Any, T_Maybe, T_Scalar } from '@tryiris-ai/js-types';
import { isNotNil } from '@tryiris-ai/js-types';
import type { Mutate } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';
import type { Draft, MutableState, State } from './types.js';
import { normalizeKey } from './utils.js';
import * as persistentLayer from '#~/persistence/index.js';

type T_InternalState = MutableState & { values: Record<string, T_Maybe<T_Scalar>> };

let seeded = false;
let seeding = false;
let seedingPromise: T_Maybe<Promise<void>> = undefined;

const persistToLayer = async (key: string, value: any) => {
  await persistentLayer.set(key, value);
};

const key = 'preferences';

// Debounce the preferences persist hop: coalesce the expensive IPC → synchronous config.json rewrite that blocked the main event loop.
// Per rule: think-in-systems (ai.knowledge/global) — fix the foot-gun at the persistence boundary.
const PERSIST_DEBOUNCE_MS = 300;
const pendingPersist = new Map<string, { value: string; timer: ReturnType<typeof setTimeout> }>();

/** Flush a single pending preference write to the persistent layer now. */
const flushPersist = (k: string): void => {
  const entry = pendingPersist.get(k);

  if (!entry) {
    return;
  }

  pendingPersist.delete(k);
  clearTimeout(entry.timer);
  // Fire-and-forget; swallow rejections (localStorage holds the value, next write re-persists).
  void persistToLayer(k, entry.value).catch(() => {
    /* best-effort */
  });
};

/** Schedule a debounced persist, replacing any earlier pending write for `k`. */
const schedulePersist = (k: string, value: string): void => {
  const existing = pendingPersist.get(k);

  if (existing) {
    clearTimeout(existing.timer);
  }

  const timer = setTimeout(() => flushPersist(k), PERSIST_DEBOUNCE_MS);
  // Guard unref() (Node timer handle only) so a pending write never keeps the process alive.
  timer.unref?.();
  pendingPersist.set(k, { value, timer });
};

// Best-effort flush of pending preference writes on window unload (not a hard durability guarantee).
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  const flushAllPending = () => {
    for (const k of [...pendingPersist.keys()]) {
      flushPersist(k);
    }
  };

  window.addEventListener('pagehide', flushAllPending);
  window.addEventListener('beforeunload', flushAllPending);
}

/** Lazily loaded preferences. */
const __preferences = new Lazy(() =>
  createStore<T_InternalState>()(
    persist(
      immer(
        subscribeWithSelector((set) => ({
          getValue(key: string): T_Maybe<T_Scalar> {
            const k = normalizeKey(key);

            return k ? this.values[k] : undefined;
          },

          hasValue(key: string): boolean {
            return isNotNil(this.getValue(key));
          },

          produce: (fn: (draft: Draft) => void): void => {
            set(fn);
          },

          setValue(key: string, value?: T_Scalar): void {
            const k = normalizeKey(key);

            if (k) {
              if (isNotNil(value)) {
                this.values[k] = value;
              } else {
                delete this.values[k];
              }
            }
          },

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          values: {} as Record<string, T_Maybe<T_Scalar>>
        }))
      ),
      {
        name: key,
        storage: {
          getItem: async (key: string) => {
            await seedLocalStorage();
            const value = localStorage.getItem(key);
            return value ? JSON.parse(value) : null;
          },
          setItem: async (key: string, value: any) => {
            await seedLocalStorage();
            const serialized = JSON.stringify(value);
            // Immediate: same-session reads (seedLocalStorage / getItem) depend on it.
            localStorage.setItem(key, serialized);
            // Debounced: coalesce the IPC → config.json rewrite (see freeze-fix note above).
            schedulePersist(key, serialized);
          },
          removeItem: async (key: string) => {
            await seedLocalStorage();
            localStorage.removeItem(key);
          }
        }
      }
    )
  )
);

/** Preferences proxy. */
export const preferences = new Proxy<
  Mutate<Store<State>, [['zustand/subscribeWithSelector', never]]>
>(<T_Any>{}, {
  get(...args): unknown {
    return Reflect.get(__preferences.value, args[1], args[2]);
  }
});

export const seedLocalStorage = async () => {
  if (seeded) {
    return;
  }

  if (seeding) {
    return await seedingPromise;
  }

  seeding = true;

  seedingPromise = new Promise<void>(async (resolve) => {
    const values = await persistentLayer.get(key);

    if (values) {
      localStorage.setItem(key, values);
    }

    resolve();
  });

  await seedingPromise;

  seeded = true;
  seeding = false;
  seedingPromise = undefined;
};
